import React, { useState, useEffect } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { InitContextProps, useAuthContext } from "../../../services/context/authentication.context";
import { IFuelInvoice } from 'models/fuel-invoice';
import { roundNumber, getVatAmount, getPriceWithoutVAT, onNumberChange } from 'helpers';
import moment from "moment";
import PaymentService from "services/payment.service";
import { BankType, DocumentPayment, IPaymentMethod, PaymentMethodType } from "models/payment";
import { toast } from "react-toastify";

interface FuelInvoicePaymentModalProps {
  visible: boolean;
  payableInvoices: IFuelInvoice[];
  handleModalHide: () => void;
  onSubmit: (paymentMethod: PaymentMethodType, documentPayments: DocumentPayment[], bankType?: BankType) => Promise<void> | void;
  paymentStatus: string | null;
}

interface PartialPaymentState {
  enabled: boolean;
  amount?: number;
}

const TOTAL_PAYABLE_AMOUNT_LIMIT = 14999.99;

const BANK_TYPE_TO_ICON_MAPPING = {
  [BankType.CITADELE] : 'citadele',
  [BankType.N26] : 'n26',
  [BankType.WISE] : 'wise',
  [BankType.BIGBANK] : 'bigbank',
};

export function FuelInvoicePaymentModal(props: FuelInvoicePaymentModalProps) {
  const { state }: InitContextProps = useAuthContext();
  const { customerId, customer } = state;

  const [saving, setSaving] = useState<string>();
  const [selectedInvoices, setSelectedInvoices]: [IFuelInvoice[], any] = useState([]);
  const [partialPayments, setPartialPayments] = useState<Record<number, PartialPaymentState>>({});
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSelectedInvoices(
      props.payableInvoices.filter(invoice => !invoice.remoteFactoring)
    );
  }, [props.payableInvoices]);

  useEffect(() => {
    getPaymentMethods();
  }, [])

  const getPaymentMethods = async () => {
    setIsLoading(true);
    try{
      const paymentMethods = await PaymentService.getPaymentMethods();
      setPaymentMethods(paymentMethods.data);
    }catch(e){
      console.error(e);
    }finally{
      setIsLoading(false);
    }
  }

  const handleSubmit = async (method: IPaymentMethod) => {
    setSaving(method.bankType);

    if (!customerId || !customer || (customer && !customer.contactId) || !selectedInvoices.length) {
      setSaving(undefined);
      return;
    }

    const documentPayments: DocumentPayment[] = selectedInvoices.map(invoice => ({
      remoteDocumentId: invoice.remoteDocumentId,
      amount: getEffectiveAmount(invoice),
    }));

    try {
      await props.onSubmit(method.type, documentPayments, method.bankType);
    } catch (e) {
      console.error(e);
      setSaving(undefined);
      toast.error('Makse alustamine ebaõnnestus.')
    }
  };

  const isInvoiceChecked = (invoice: IFuelInvoice) => {
    return !!selectedInvoices.find(i => i.remoteDocumentId === invoice.remoteDocumentId);
  }

  const onCheckboxToggle = (invoice: IFuelInvoice) => {
    if (!isInvoiceChecked(invoice)) {
      setSelectedInvoices([...selectedInvoices, invoice]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(i => i.remoteDocumentId !== invoice.remoteDocumentId));
    }
  }

  const getOutstandingAmount = (invoice: IFuelInvoice) => roundNumber(invoice.totalAmount - invoice.paymentAmount, 2);

  const isPartialEnabled = (invoice: IFuelInvoice) => !!partialPayments[invoice.remoteDocumentId]?.enabled;

  const getPartialAmount = (invoice: IFuelInvoice) => partialPayments[invoice.remoteDocumentId]?.amount;

  const getEffectiveAmount = (invoice: IFuelInvoice) => {
    if (isPartialEnabled(invoice)) return getPartialAmount(invoice) || 0;
    return getOutstandingAmount(invoice);
  };

  const onPartialToggle = (invoice: IFuelInvoice) => {
    const enabled = !isPartialEnabled(invoice);
    setPartialPayments({
      ...partialPayments,
      [invoice.remoteDocumentId]: {
        enabled,
        amount: enabled ? getOutstandingAmount(invoice) : undefined,
      },
    });
  };

  const onPartialAmountChange = (invoice: IFuelInvoice, amount?: number) => {
    setPartialPayments({
      ...partialPayments,
      [invoice.remoteDocumentId]: {
        enabled: true,
        amount: amount === undefined ? undefined : roundNumber(amount, 2),
      },
    });
  };

  if (!props.visible) return null;

  const mappedInvoices = props.payableInvoices.map((invoice, index) => {
    const amountWithoutVat = getPriceWithoutVAT(invoice.totalAmount - invoice.paymentAmount, moment(invoice.documentDateCreated), 2);
    const amountWithVat = roundNumber(invoice.totalAmount - invoice.paymentAmount, 2);
    const amountVat = getVatAmount(amountWithVat, moment(invoice.documentDateCreated), 2);
    const partialEnabled = isPartialEnabled(invoice);
    const partialAmount = getPartialAmount(invoice);
    const partialInvalid = partialEnabled && !(Number(partialAmount) > 0);

    return (
      <div key={invoice.remoteDocumentId} className="card d-flex flex-column p-0">
        <div className="d-flex align-items-center flex-row">
          <div className="d-flex col-6">
            <div className="custom-control custom-checkbox mr-sm-2">
              <input
                type="checkbox"
                className="custom-control-input"
                id={'check_' + invoice.remoteDocumentId}
                checked={isInvoiceChecked(invoice)}
                onChange={() => onCheckboxToggle(invoice)}
                disabled={!!invoice.remoteFactoring}
                />
              <label className="custom-control-label" htmlFor={'check_' + invoice.remoteDocumentId}></label>
            </div>
            <div>{invoice.documentNumber}</div>
          </div>
          <div className="col-2 text-right">{amountWithoutVat} €</div>
          <div className="col-2 text-right">{amountVat} €</div>
          <div className="col-2 text-right">{amountWithVat} €</div>
        </div>
        { isInvoiceChecked(invoice) && (
          <div className="d-flex align-items-center flex-row mt-2">
            <div className="d-flex col-6">
              <div className="custom-control custom-checkbox mr-sm-2 invisible">
                <input type="checkbox" className="custom-control-input" id={'partial_spacer_' + invoice.remoteDocumentId} disabled />
                <label className="custom-control-label" htmlFor={'partial_spacer_' + invoice.remoteDocumentId}></label>
              </div>
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={'partial_' + invoice.remoteDocumentId}
                  checked={partialEnabled}
                  onChange={() => onPartialToggle(invoice)}
                  />
                <label className="custom-control-label" htmlFor={'partial_' + invoice.remoteDocumentId}>Maksa osaliselt</label>
              </div>
            </div>
            {partialEnabled && (
              <div className="col-6 text-right">
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className={'form-control text-right' + (partialInvalid ? ' is-invalid' : '')}
                  value={partialAmount}
                  onChange={e => onPartialAmountChange(invoice, onNumberChange(e))}
                />
                {partialInvalid && (
                  <small className="text-danger">Summa peab olema suurem kui 0</small>
                )}
              </div>
            )}
          </div>
        )}
        {index < props.payableInvoices.length - 1 && <hr className="w-100 my-2" />}
      </div>
    );
  });

  const sumWithVat = roundNumber(
    selectedInvoices.reduce((acc, curr) => acc + getEffectiveAmount(curr), 0),
    2
  );

  const hasInvalidPartialAmount = selectedInvoices.some(
    invoice => isPartialEnabled(invoice) && !(Number(getPartialAmount(invoice)) > 0)
  );

  const sumVat = roundNumber(
    selectedInvoices.reduce((acc, curr) => acc + getVatAmount(
      Math.min(getEffectiveAmount(curr), getOutstandingAmount(curr)),
      moment(curr.documentDateCreated),
      2,
    ), 0), 2
  );

  const noSelectedInvoices = !selectedInvoices.length;

  const bankPaymentMethods = paymentMethods.filter(method => method.type === PaymentMethodType.BANK);
  const cardPaymentMethod = paymentMethods.filter(method => method.type === PaymentMethodType.CARD)[0];

  const isPayable = sumWithVat <= TOTAL_PAYABLE_AMOUNT_LIMIT;

  return (
      <Modal
        show={props.visible}
        size={"lg"}
        onHide={props.handleModalHide}
        dialogClassName="fuel-invoice-payment-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Arve tasumine
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-row mb-1">
            <div className="col-8 text-right pr-3">
              <span>Summa km-ta</span>
            </div>
            <div className="col-2 text-right pr-3">
              <span>Käibemaks</span>
            </div>
            <div className="col-2 text-right pr-3">
              <span>Summa km-ga</span>
            </div>
          </div>
          <div className="form-row mt-2">
            <div className="col-12 d-flex flex-column">
              { mappedInvoices }
            </div>
          </div>
          <div className="form-row">
            <div className="col-12 text-right pr-3">
              Käibemaks kokku: {sumVat} €
            </div>
            <div className="col-12 text-right pr-3">
              Tasumisele kuulub: {sumWithVat} €
            </div>
          </div>
        </Modal.Body>
          <Modal.Footer className="d-flex flex-column align-items-start justify-content-center w-full">
            {isPayable ? (
               <div className="w-full">
                <h5 className="mb-2">
                  Alusta makset:
                </h5>
                <div className="d-flex flex-row align-items-center justify-content-center flex-wrap mb-1">
                  {bankPaymentMethods?.map(method => (
                    <PaymentMethod 
                      method={method}
                      handleSubmit={handleSubmit}
                      isDisabled={!!saving || noSelectedInvoices || hasInvalidPartialAmount}
                      saving={saving}
                    />
                  ))}
                </div>
                <div className="d-flex align-items-center justify-content-center">
                  {!!cardPaymentMethod && (
                    <PaymentMethod 
                      method={cardPaymentMethod}
                      handleSubmit={handleSubmit}
                      isDisabled={!!saving || noSelectedInvoices || hasInvalidPartialAmount}
                      saving={saving}
                    />
                  )}
                </div>
                <div className="m-auto">
                  {isLoading && (
                      <Spinner size="sm" animation="border"/>
                  )}
                </div>
               </div>
            ) : <div className="text-danger">
                Pangalingi maksed on limteeritud 14999.99 eurole!
              </div>}
          </Modal.Footer>
      </Modal>
  )
}

interface PaymentMethodProps {
  method : IPaymentMethod;
  handleSubmit: (method: IPaymentMethod) => void;
  isDisabled: boolean;
  saving?: string
}

function PaymentMethod({method, saving, isDisabled, handleSubmit} : PaymentMethodProps){
  return (
    <Button
      variant="outline-light"
      disabled={isDisabled}
      key={method.bankType}
      className={`fuel-invoice-payment-modal__${method.type === PaymentMethodType.BANK ? 'bank-link' : 'card-link'} mr-2 mb-2 d-flex justify-content-center align-items-center`}
      onClick={() => handleSubmit(method)}
    >
      {saving === method.bankType ? (
        <div>
          <Spinner size={"sm"} animation={"border"} style={{width: 20, height: 20}}/>
        </div>
      ) : (
        <>
          {method.bankType === BankType.CITADELE || method.bankType === BankType.N26 || method.bankType === BankType.WISE || method.bankType === BankType.BIGBANK ? (
            <i className={`icon--60 icon__${BANK_TYPE_TO_ICON_MAPPING[method.bankType]}`}></i>
          ) : (
            <img src={method.logoUrl} alt={method.name}/>
          )}
        </>
      )} 
    </Button>
  )
}